import { Card, Input, Radio, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { CoverageCalculationBreakdown } from 'features/libra/stepComponents/zoneAnalysis/components/CoverageCalculationBreakdown'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  getFAcc,
  getFCov,
  getFLc,
  getFSh,
  getFZone,
  InspectionStrategy,
  RbaInformation,
  TriState,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { AlertCircle, Info, Maximize2 } from 'lucide-react'
import { FC, ReactNode } from 'react'
import styled, { useTheme } from 'styled-components'

const { TextArea } = Input

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 16px 0;
`

const FactorCard = styled(Card)`
  .ant-card-head {
    background: ${({ theme }) => theme.forecast.background2};
  }
`

const FactorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ModifierDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`

const ModifierValue = styled.span<{ $value: number }>`
  font-weight: 600;
  font-size: 18px;
  color: ${({ $value }) =>
    $value > 1 ? '#f44336' : $value < 1 ? '#4caf50' : '#666'};
`

const ModifierLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

interface SimpleFactorEditorProps {
  title: ReactNode
  description: ReactNode
  value?: TriState
  justification?: string
  onChange: (value: TriState) => void
  onJustificationChange?: (justification: string) => void
  lowLabel: string
  lowModifier: number
  mediumLabel: string
  mediumModifier: number
  highLabel: string
  highModifier: number
  readonly?: boolean
}

const SimpleFactorEditor: FC<SimpleFactorEditorProps> = ({
  title,
  description,
  value,
  justification,
  onChange,
  onJustificationChange,
  lowLabel,
  lowModifier,
  mediumLabel,
  mediumModifier,
  highLabel,
  highModifier,
  readonly,
}) => {
  const theme = useTheme()
  const currentValue = value ?? 'medium'
  const currentModifier =
    currentValue === 'low'
      ? lowModifier
      : currentValue === 'high'
        ? highModifier
        : mediumModifier

  const modifierImpact =
    currentModifier > 1
      ? 'Increases required coverage'
      : currentModifier < 1
        ? 'Decreases required coverage'
        : 'Neutral impact'

  return (
    <FactorCard title={title} size='small'>
      <FactorContent>
        <div style={{ fontSize: 13, color: theme.forecast.secondaryText }}>
          {description}
        </div>

        <Radio.Group
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={readonly}>
          <Radio value='low'>
            {lowLabel}{' '}
            <Tag
              color={
                lowModifier > 1 ? 'red' : lowModifier < 1 ? 'green' : 'gold'
              }>
              {lowModifier}×
            </Tag>
          </Radio>
          <Radio value='medium'>
            {mediumLabel}{' '}
            <Tag
              color={
                mediumModifier > 1
                  ? 'red'
                  : mediumModifier < 1
                    ? 'green'
                    : 'gold'
              }>
              {mediumModifier}×
            </Tag>
          </Radio>
          <Radio value='high'>
            {highLabel}{' '}
            <Tag
              color={
                highModifier > 1 ? 'red' : highModifier < 1 ? 'green' : 'gold'
              }>
              {highModifier}×
            </Tag>
          </Radio>
        </Radio.Group>

        <ModifierDisplay>
          <ModifierValue $value={currentModifier}>
            {currentModifier}×
          </ModifierValue>
          <ModifierLabel>{modifierImpact}</ModifierLabel>
          <Tag
            color={
              currentModifier > 1
                ? 'red'
                : currentModifier < 1
                  ? 'green'
                  : 'gold'
            }>
            {currentModifier > 1 ? '↑' : currentModifier < 1 ? '↓' : '→'}
          </Tag>
        </ModifierDisplay>

        {onJustificationChange && (
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                display: 'block',
                marginBottom: 4,
              }}>
              Justification
            </label>
            <TextArea
              value={justification}
              onChange={(e) => onJustificationChange(e.target.value)}
              placeholder='Explain the reasoning for this selection...'
              rows={2}
              disabled={readonly}
            />
          </div>
        )}
      </FactorContent>
    </FactorCard>
  )
}

const EmptyStateCard = styled(Card)`
  .ant-card-body {
    padding: 40px;
    text-align: center;
  }
`

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  opacity: 0.5;
`

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 8px;
`

const EmptyStateText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ConfidenceDisplay = styled(Card)`
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  margin-bottom: 16px;

  .ant-card-body {
    padding: 12px 16px;
  }
`

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
`

const ConfidenceLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ConfidenceValue = styled.span<{ $level: 'low' | 'med' | 'high' }>`
  font-weight: 600;
  text-transform: capitalize;
  color: ${({ $level }) =>
    $level === 'high' ? '#52c41a' : $level === 'med' ? '#faad14' : '#ff4d4f'};
`

export interface RbaInformationPanelProps {
  zone: ZoneParams
  onChange: (updates: Partial<ZoneParams>) => void
  readonly?: boolean
}

export const RbaInformationPanel: FC<RbaInformationPanelProps> = ({
  zone,
  onChange,
  readonly,
}) => {
  const anchor = useZonePropertyAnchor(zone.id)
  const { confidence, cof } = useZoneAnalysisData()
  const rbaInfo = zone.rba_info
  const strategyType = getEffectiveStrategyType(zone.nii_strategy)
  const zoneArea = getEffectiveZoneArea(zone)

  const handleFieldChange = (field: keyof RbaInformation, value: TriState) => {
    const updatedRba = {
      ...rbaInfo,
      [field]: value,
    }

    // Recalculate coverage and save both RBA and coverage
    const updatedZone = { ...zone, rba_info: updatedRba }
    const newCoverage = calculateRequiredCoverage(
      updatedZone,
      getCofModifier(cof.level),
      confidence
    )

    onChange({
      rba_info: updatedRba,
      coverage_calculation: newCoverage,
    })
  }

  const handleJustificationChange = (
    field: keyof RbaInformation,
    justification: string
  ) => {
    onChange({
      rba_info: {
        ...rbaInfo,
        [field]: justification,
      },
    })
  }

  // No strategy selected yet
  if (!strategyType) {
    return (
      <Panel>
        <EmptyStateCard>
          <EmptyStateIcon>
            <AlertCircle size={48} />
          </EmptyStateIcon>
          <EmptyStateTitle>Strategy Not Selected</EmptyStateTitle>
          <EmptyStateText>
            Please complete the <strong>NII Strategy</strong> tab first to
            determine which risk factors need to be assessed.
          </EmptyStateText>
        </EmptyStateCard>
      </Panel>
    )
  }

  // Type C: No RBA needed
  if (strategyType === InspectionStrategy.C) {
    return (
      <Panel>
        <EmptyStateCard>
          <EmptyStateIcon>✓</EmptyStateIcon>
          <EmptyStateTitle>No Risk Assessment Required</EmptyStateTitle>
          <EmptyStateText>
            Type C inspection strategy always requires{' '}
            <strong>100% coverage</strong>. No additional risk factors need to
            be configured.
          </EmptyStateText>
        </EmptyStateCard>
      </Panel>
    )
  }

  // Calculate F_COV preview for display
  const density = rbaInfo?.density_of_degradation ?? 'medium'
  const fCovPreview = getFCov(confidence, density)
  const fZone = getFZone(zoneArea)

  // Calculate coverage to show results
  const calculatedCoverage = calculateRequiredCoverage(
    zone,
    getCofModifier(cof.level),
    confidence
  )

  // Type A or B: Show confidence from screening (read-only)
  return (
    <Panel>
      <HelpText>
        Configure the risk factors that determine inspection coverage
        requirements for this zone. The factors vary based on the inspection
        strategy type.
      </HelpText>

      {/* Display confidence from NII Screening (read-only) */}
      <ConfidenceDisplay>
        <ConfidenceRow>
          <Info size={16} />
          <ConfidenceLabel>
            Confidence in Prediction (from NII Screening):
          </ConfidenceLabel>
          <ConfidenceValue $level={confidence}>
            {confidence === 'med' ? 'Medium' : confidence}
          </ConfidenceValue>
          <Tag
            color={
              confidence === 'high'
                ? 'green'
                : confidence === 'med'
                  ? 'gold'
                  : 'red'
            }>
            Read-only
          </Tag>
        </ConfidenceRow>
      </ConfidenceDisplay>

      {/* Zone Area Modifier (read-only display) */}
      <ConfidenceDisplay>
        <ConfidenceRow>
          <Maximize2 size={16} />
          <ConfidenceLabel>Zone Area: {zoneArea.toFixed(2)} m²</ConfidenceLabel>
          <Tag color={fZone > 1 ? 'red' : fZone < 1 ? 'green' : 'gold'}>
            F<sub>ZONE</sub> = {fZone}×
          </Tag>
        </ConfidenceRow>
      </ConfidenceDisplay>

      {/* Density - Required for both Type A and Type B */}
      <CommentIndicator anchor={anchor('rba_info.density_of_degradation')}>
        <SimpleFactorEditor
          title='Density of Degradation'
          description={
            <>
              How densely are degradation features distributed? (Combined with
              Confidence=&quot;{confidence}&quot; for F<sub>COV</sub> ={' '}
              {fCovPreview}×)
            </>
          }
          value={rbaInfo?.density_of_degradation}
          justification={rbaInfo?.density_justification}
          onChange={(v) => handleFieldChange('density_of_degradation', v)}
          onJustificationChange={(j) =>
            handleJustificationChange('density_justification', j)
          }
          lowLabel='Low - Sparse/isolated'
          lowModifier={getFCov(confidence, 'low')}
          mediumLabel='Medium - Moderate'
          mediumModifier={getFCov(confidence, 'medium')}
          highLabel='High - Widespread'
          highModifier={getFCov(confidence, 'high')}
          readonly={readonly}
        />
      </CommentIndicator>

      {/* Type B Additional Factors */}
      {strategyType === InspectionStrategy.B && (
        <>
          <CommentIndicator anchor={anchor('rba_info.measurement_accuracy')}>
            <SimpleFactorEditor
              title={
                <>
                  Measurement Accuracy (F<sub>ACC</sub>)
                </>
              }
              description='What is the accuracy of the planned inspection technique?'
              value={rbaInfo?.measurement_accuracy}
              justification={rbaInfo?.measurement_accuracy_justification}
              onChange={(v) => handleFieldChange('measurement_accuracy', v)}
              onJustificationChange={(j) =>
                handleJustificationChange(
                  'measurement_accuracy_justification',
                  j
                )
              }
              lowLabel='Low - Visual only'
              lowModifier={getFAcc('low')}
              mediumLabel='Medium - Standard UT/PT/MT'
              mediumModifier={getFAcc('medium')}
              highLabel='High - Advanced/Robotic'
              highModifier={getFAcc('high')}
              readonly={readonly}
            />
          </CommentIndicator>

          <CommentIndicator
            anchor={anchor('rba_info.tolerance_of_degradation')}>
            <SimpleFactorEditor
              title={
                <>
                  Tolerance to Degradation (F<sub>LC</sub>)
                </>
              }
              description='How much degradation can the vessel tolerate before becoming unfit for service?'
              value={rbaInfo?.tolerance_of_degradation}
              justification={rbaInfo?.tolerance_justification}
              onChange={(v) => handleFieldChange('tolerance_of_degradation', v)}
              onJustificationChange={(j) =>
                handleJustificationChange('tolerance_justification', j)
              }
              lowLabel='Low - Near retirement'
              lowModifier={getFLc('low')}
              mediumLabel='Medium - Normal margin'
              mediumModifier={getFLc('medium')}
              highLabel='High - Large margin'
              highModifier={getFLc('high')}
              readonly={readonly}
            />
          </CommentIndicator>

          <CommentIndicator anchor={anchor('rba_info.spatial_homogeneity')}>
            <SimpleFactorEditor
              title={
                <>
                  Spatial Homogeneity (F<sub>SH</sub>)
                </>
              }
              description='How uniformly is degradation distributed across the zone?'
              value={rbaInfo?.spatial_homogeneity}
              justification={rbaInfo?.spatial_homogeneity_justification}
              onChange={(v) => handleFieldChange('spatial_homogeneity', v)}
              onJustificationChange={(j) =>
                handleJustificationChange(
                  'spatial_homogeneity_justification',
                  j
                )
              }
              lowLabel='Low - Highly variable'
              lowModifier={getFSh('low')}
              mediumLabel='Medium - Moderately uniform'
              mediumModifier={getFSh('medium')}
              highLabel='High - Completely uniform'
              highModifier={getFSh('high')}
              readonly={readonly}
            />
          </CommentIndicator>
        </>
      )}

      {calculatedCoverage && strategyType && (
        <CoverageCalculationBreakdown
          zone={zone}
          coverage={calculatedCoverage}
          strategyType={strategyType}
          confidence={confidence}
        />
      )}
    </Panel>
  )
}
